import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import { voiceModelService } from '../Services/voiceModelService';
import { PaginationListType } from '../Types/PaginationParams';
import { setListReloadType } from '../Store/features/environment/environmentSlice';
import useModal from '../components/Modal/useModal';
import { VoiceModel } from '../Types';
import { getBreakpoint } from '../utils/breakpoint';

const useVoiceModels = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useAppDispatch();
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const modal = useModal();

    const deleteModelfunction = useCallback(
        (identifier: string) => {
            if (!userSettings.isLoggedIn) return;
            voiceModelService.deleteModel(identifier).then(() => {
                dispatch(setListReloadType(PaginationListType.VoiceModels));
            });
        },
        [userSettings.isLoggedIn, dispatch],
    );

    const handleOpenConfirmDialog = useCallback(
        (apiKeyIdentifier: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'modelDelete.title', sourceName: 'Models' }),
                content: i18n.translate({ id: 'modelDelete.text', sourceName: 'Models' }),
                onConfirm: () => deleteModelfunction(apiKeyIdentifier),
            });
        },
        [modal, deleteModelfunction, i18n],
    );

    const handleCopyModel = useCallback(
        async (model: VoiceModel) => {
            const response = await voiceModelService.getModel(model.identifier);
            const fullModelData = response.data;

            const modelCopy = {
                ...fullModelData,
                title: `${fullModelData.title} ${i18n.translate({
                    id: 'duplicate.copyTag',
                    sourceName: 'Main',
                })}`,
                identifier: undefined,
            };

            navigate('/admin/voiceModels/new', { state: { duplicateData: modelCopy } });
        },
        [navigate, i18n],
    );

    const ModelsActions = useMemo(
        () => ({
            handleOpenConfirmDialog,
        }),
        [handleOpenConfirmDialog],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        ModelsActions,
        handleCopyModel,
        handleNavigate,
        i18n,
        userSettings,
        windowWidth,
        getBreakpoint,
    };
};

export default useVoiceModels;
